import { Array, pipe } from 'effect'

import type { ViewQueryNode, ViewQuerySorter } from '../../../../schemas/query/index.ts'
import type { ViewConfigSheet } from '../../../../schemas/view_config/index.ts'
import type { CompileContext } from '../../common.ts'
import type { FieldAndColumn } from './compileSheetViewConfigFields.ts'
import { isSameNodeProperty } from './isSameNodeProperty.ts'

export function compileSheetViewConfigSorters(
  config: ViewConfigSheet,
  nodes: ViewQueryNode[],
  fieldsAndColumns: FieldAndColumn[],
  context: CompileContext,
): ViewQuerySorter[] {
  const sorters: ViewConfigSheet['sorters'] = config.sorters
  if (sorters === undefined) {
    return []
  }

  return pipe(
    sorters,
    Array.flatMapNullishOr((sorter): ViewQuerySorter | undefined => {
      // fieldに存在しないsorterが定義されてしまった場合除外する(通常あり得ない)
      if (
        config.fields?.find(
          (field) =>
            field.property.nodeName === sorter.property.nodeName &&
            field.property.propertyName === sorter.property.propertyName,
        ) === undefined
      ) {
        return undefined
      }
      const node = nodes.find((node) => node.name === sorter.property.nodeName)
      const property = context.modelSearcher.searchProperty(sorter.property.modelName, sorter.property.propertyName)
      if (node === undefined || property === undefined) {
        return undefined
      }

      const sortBy = sorter.sortBy ?? 'value'
      const fieldAndColumn = fieldsAndColumns.find((fieldAndColumn) =>
        isSameNodeProperty(fieldAndColumn.configField.property, sorter.property),
      )
      const labelSql = sortBy === 'label' ? fieldAndColumn?.queryField.read.labelSql : undefined
      if (sortBy === 'label' && labelSql === undefined) {
        // この warn は showWarns() 経由でシートの「エラー一覧」ドロワーに素通しで出るため、ユーザー向け日本語にする。
        context.logs.warn(`「${property.label}」は表示名による並び替えができないため、IDで並び替えます`)
      }

      return {
        nodePaths: [node.path],
        read: {
          // eslint-disable-next-line sonarjs/todo-tag
          sql: `"${node.name}"."${property.name}"`, // TODO: このレイヤーでSQL変換したくない
          labelSql,
          order: sorter.order,
        },
        meta: {
          label: [property.label, sorter.order === 'desc' ? ' 降順' : ''].join(''),
          dependedPropertyNamesWithStreamName: [
            {
              streamName: sorter.property.modelName,
              propertyName: sorter.property.propertyName,
            },
          ],
        },
      }
    }),
  )
}
